import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormGroup,
    Checkbox,
    Box,
    Typography,
    TextField,
    Slide,
} from "@mui/material";
import { FC, useRef, useState } from "react";
import PrintIcon from '@mui/icons-material/Print';
import StoreIcon from '@mui/icons-material/Store';
import SendIcon from '@mui/icons-material/Send';
import { useToast } from "../../../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { proformaEmail } from "../../../config";

interface InvoiceActionDialogProps {
    clientEmailValue?: string;
    open: boolean;
    onClose: () => void;
    onPrint: () => void;
    onSendToShowroom: (email: string) => void;
    onSendToClient: (email: string) => void;
    resetInvoiceData: () => void; 
}

const options = [
    {
        label: "Drukuj",
        color: "#bfa14a",
        icon: <PrintIcon fontSize="large" />,
        value: "print",
    },
    {
        label: "Wyślij do salonu",
        color: "#bfa14a",
        icon: <StoreIcon fontSize="large" />,
        value: "sendToShowroom",
    },
    {
        label: "Wyślij do klienta",
        color: "#bfa14a",
        icon: <SendIcon fontSize="large" />,
        value: "sendToClient",
    },
];

const InvoiceActionDialog: FC<InvoiceActionDialogProps> = ({
    clientEmailValue,
    open,
    onPrint,
    onSendToShowroom,
    onSendToClient,
    onClose,
    resetInvoiceData,
}) => {
    const [checked, setChecked] = useState<string[]>(["print", "sendToShowroom"]);
    const [clientEmail, setClientEmail] = useState(clientEmailValue || "");
    const toast = useRef(useToast()).current;
    const navigate = useNavigate()

    const handleChange = (option: string) => {
        setChecked(prev =>
            prev.includes(option)
                ? prev.filter(o => o !== option)
                : [...prev, option]
        );
    };

    const handleSend = () => {
        if (checked.includes("sendToClient") && !clientEmail.trim()) {
            toast.error("Podaj adres e-mail klienta, aby wysłać fakturę.");
            return;
        }
        if (checked.includes('print')) onPrint();
        if (checked.includes('sendToShowroom')) onSendToShowroom(proformaEmail || "");
        if (checked.includes('sendToClient')) onSendToClient(clientEmail);
        navigate('/proformy')
        onClose();
        resetInvoiceData();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            TransitionComponent={Slide}
            PaperProps={{
                sx: {
                    borderRadius: 6,
                    boxShadow: "0 8px 32px 0 rgba(191,161,74,0.12)",
                    background: "#fff",
                    p: 0,
                    minWidth: 480,
                    overflow: "hidden",
                }
            }}
        >
            <Box sx={{
                background: "linear-gradient(90deg, #fffbe6 0%, #fff 100%)",
                py: 3,
                px: 5,
                textAlign: "center",
                borderBottom: "1px solid #f5e9c7"
            }}>
                <DialogTitle sx={{
                    fontWeight: 700,
                    fontSize: 28,
                    letterSpacing: 2,
                    color: "#bfa14a",
                    m: 0,
                    p: 0,
                }}>
                    Wybierz akcję dla faktury
                </DialogTitle>
            </Box>
            <DialogContent sx={{ px: 5, pt: 4, pb: 2 }}>
                <FormGroup>
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        alignItems: "center",
                        mt: 2,
                    }}>
                        {options.map(opt => (
                            <Box
                                key={opt.value}
                                sx={{
                                    width: "100%",
                                    maxWidth: 340,
                                    px: 2,
                                    py: 2,
                                    borderRadius: 4,
                                    background: checked.includes(opt.value)
                                        ? "linear-gradient(90deg, #fffbe6 0%, #f5e9c7 100%)"
                                        : "#fff",
                                    boxShadow: checked.includes(opt.value)
                                        ? `0 2px 16px 0 #bfa14a33`
                                        : "0 1px 6px 0 rgba(0,0,0,0.04)",
                                    border: checked.includes(opt.value)
                                        ? "1.5px solid #bfa14a"
                                        : "1.5px solid #f5e9c7",
                                    transition: "box-shadow 0.2s, background 0.2s, border 0.2s",
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    position: "relative",
                                    opacity: checked.includes(opt.value) ? 1 : 0.96,
                                    "&:hover": {
                                        boxShadow: `0 4px 24px 0 #bfa14a55`,
                                        opacity: 1,
                                        border: "1.5px solid #bfa14a",
                                        background: "linear-gradient(90deg, #fffbe6 0%, #f5e9c7 100%)",
                                    }
                                }}
                                onClick={() => handleChange(opt.value)}
                            >
                                <Checkbox
                                    checked={checked.includes(opt.value)}
                                    icon={opt.icon}
                                    checkedIcon={opt.icon}
                                    sx={{
                                        color: "#bfa14a",
                                        '&.Mui-checked': {
                                            color: "#bfa14a",
                                            backgroundColor: "transparent",
                                        },
                                        mr: 1.5,
                                        p: 0.5,
                                        fontSize: 32,
                                    }}
                                />
                                <Typography sx={{
                                    fontWeight: 700,
                                    color: checked.includes(opt.value) ? "#bfa14a" : "#bfa14a",
                                    fontSize: 18,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    letterSpacing: 1,
                                }}>
                                    {opt.label}
                                </Typography>
                                {checked.includes(opt.value) && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 10,
                                            right: 14,
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            background: "#bfa14a",
                                            boxShadow: `0 0 10px 2px #bfa14a55`,
                                            border: "1.5px solid #fff",
                                        }}
                                    />
                                )}
                            </Box>
                        ))}
                    </Box>
                    {checked.includes("sendToClient") && (
                        <TextField
                            label="E-mail klienta"
                            type="email"
                            fullWidth
                            value={clientEmail}
                            onChange={e => setClientEmail(e.target.value)}
                            sx={{
                                mt: 3,
                                mb: 1,
                                background: "#fff",
                                borderRadius: 3,
                                boxShadow: "0 2px 8px 0 #bfa14a22",
                                fontSize: 18,
                                "& .MuiInputLabel-root": {
                                    fontWeight: 700,
                                    color: "#bfa14a",
                                },
                                "& .MuiInputBase-input": {
                                    fontWeight: 700,
                                    fontSize: 18,
                                }
                            }}
                        />
                    )}
                </FormGroup>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 5, pb: 3 }}>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={onClose}
                    sx={{
                        fontWeight: 700,
                        borderRadius: 4,
                        boxShadow: "0 1px 6px 0 #bfa14a22",
                        background: "#fff",
                        fontSize: 16,
                        letterSpacing: 1,
                        px: 3,
                        textTransform: "uppercase",
                        border: "1.5px solid #bfa14a",
                        color: "#bfa14a",
                        "&:hover": {
                            background: "#fffbe6",
                        }
                    }}
                >
                    Wróć do faktury
                </Button>
                <Button
                    variant="contained"
                    color="inherit"
                    sx={{
                        fontWeight: 700,
                        borderRadius: 4,
                        boxShadow: "0 2px 12px 0 #bfa14a44",
                        background: "linear-gradient(90deg, #bfa14a 0%, #fffbe6 100%)",
                        fontSize: 16,
                        letterSpacing: 1,
                        px: 4,
                        textTransform: "uppercase",
                        border: "1.5px solid #bfa14a",
                        color: "#fff",
                        "&:hover": {
                            background: "linear-gradient(90deg, #fffbe6 0%, #bfa14a 100%)",
                            color: "#bfa14a",
                        }
                    }}
                    onClick={handleSend}
                >
                    Dalej
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoiceActionDialog;